<template>
  <main class="fixed-page">
    <header class="fixed-header">
      <div>
        <span class="fixed-eyebrow">Sua rotina financeira</span>
        <h1>Compromissos fixos</h1>
      </div>

      <div class="fixed-header__actions">
        <FinancialPrivacyButton />
        <button
          class="refresh-button"
          type="button"
          :disabled="getLoadingFinance"
          aria-label="Atualizar compromissos"
          title="Atualizar compromissos"
          @click="loadData"
        >
          <i class="ph ph-arrow-clockwise" :class="{ 'is-spinning': getLoadingFinance }" aria-hidden="true"></i>
        </button>
      </div>
    </header>

    <div class="fixed-workspace">
      <div class="estimate-card" aria-labelledby="estimate-title">
        <div class="estimate-card__content">
          <span class="estimate-card__label">Estimativa mensal</span>
          <h2 id="estimate-title">{{ privateValue(monthlyEstimate) }}</h2>
          <p>Média dos seus compromissos semanais, mensais e anuais.</p>

          <small v-if="unestimatedCount" class="estimate-warning">
            {{ unestimatedCount }} {{ unestimatedCount === 1 ? 'item não entrou' : 'itens não entraram' }} na estimativa por possuir frequência personalizada.
          </small>
        </div>

        <button
          class="estimate-card__aside estimate-card__add"
          type="button"
          aria-label="Cadastrar novo compromisso"
          title="Novo compromisso"
          @click="openCreateModal"
        >
          <span><i class="ph ph-plus" aria-hidden="true"></i></span>
          <div>
            <small>Cadastrar</small>
            <strong>Novo compromisso</strong>
          </div>
        </button>
      </div>

      <section class="agenda-panel" aria-labelledby="agenda-title">
        <div class="agenda-heading">
          <div>
            <h2 id="agenda-title">Seus compromissos: {{ activeCount }}</h2>
            <p>{{ agendaView === 'calendar' ? calendarSummary : paginationSummary }}</p>
          </div>
          <div class="agenda-heading__actions">
            <span v-if="getFinanceRecurringExpenses.length && agendaView === 'list'" class="agenda-heading__hint">
              Toque para ver detalhes
              <i class="ph ph-caret-right" aria-hidden="true"></i>
            </span>
            <button
              class="agenda-view-toggle"
              type="button"
              :aria-label="agendaView === 'list' ? 'Ver compromissos no calendário' : 'Ver compromissos em lista'"
              :title="agendaView === 'list' ? 'Visualização em calendário' : 'Visualização em lista'"
              @click="toggleAgendaView"
            >
              <i :class="agendaView === 'list' ? 'ph ph-calendar-blank' : 'ph ph-list-bullets'" aria-hidden="true"></i>
            </button>
          </div>
        </div>

      <nav class="agenda-tabs" role="tablist" aria-label="Filtrar compromissos">
        <button
          v-for="tab in agendaTabs"
          :key="tab.value"
          type="button"
          role="tab"
          :class="{ active: filters.status === tab.value }"
          :aria-selected="filters.status === tab.value"
          @click="changeAgendaTab(tab.value)"
        >
          {{ tab.label }}
          <span>{{ tab.count }}</span>
        </button>
      </nav>

      <div v-if="getFinanceError" class="fixed-feedback fixed-feedback--error" role="alert">
        <div>
          <i class="ph ph-warning-circle" aria-hidden="true"></i>
          <span>{{ getFinanceError }}</span>
        </div>
        <button type="button" @click="loadData">Tentar novamente</button>
      </div>

      <div v-if="getLoadingFinance" class="fixed-skeletons" role="status" aria-label="Carregando compromissos">
        <div v-for="item in 4" :key="item" class="fixed-skeleton">
          <span class="skeleton skeleton--date"></span>
          <span class="skeleton skeleton--content"></span>
          <span class="skeleton skeleton--amount"></span>
        </div>
      </div>

      <template v-else-if="agendaView === 'calendar'">
        <div class="calendar-view" :class="{ 'has-selection': selectedCalendarDay }">
          <header class="calendar-toolbar">
            <button type="button" aria-label="Mês anterior" @click="changeCalendarMonth(-1)">
              <i class="ph ph-caret-left" aria-hidden="true"></i>
            </button>
            <strong>{{ calendarMonthLabel }}</strong>
            <button type="button" aria-label="Próximo mês" @click="changeCalendarMonth(1)">
              <i class="ph ph-caret-right" aria-hidden="true"></i>
            </button>
          </header>

          <div v-if="calendarError" class="fixed-feedback fixed-feedback--error" role="alert">
            <div>
              <i class="ph ph-warning-circle" aria-hidden="true"></i>
              <span>{{ calendarError }}</span>
            </div>
            <button type="button" @click="loadCalendarData">Tentar novamente</button>
          </div>

          <div v-else-if="calendarLoading" class="calendar-loading" role="status" aria-label="Carregando calendário">
            <span v-for="item in 35" :key="item" class="skeleton"></span>
          </div>

          <template v-else>
            <div class="calendar-grid" role="grid" :aria-label="`Compromissos de ${calendarMonthLabel}`">
              <span v-for="weekday in calendarWeekdays" :key="weekday" class="calendar-weekday" role="columnheader">{{ weekday }}</span>
              <button
                v-for="day in calendarDays"
                :key="day.key"
                type="button"
                class="calendar-day"
                :class="{
                  'is-outside': !day.inMonth,
                  'is-today': day.isToday,
                  'is-past': day.isPast,
                  'has-items': day.items.length,
                  'is-selected': selectedCalendarDateKey === day.key,
                }"
                :aria-label="calendarDayAriaLabel(day)"
                :aria-pressed="selectedCalendarDateKey === day.key"
                @click="selectCalendarDay(day)"
              >
                <span>{{ day.day }}</span>
                <strong v-if="day.items.length">{{ day.items.length }}</strong>
              </button>
            </div>

            <div v-if="selectedCalendarDay" ref="calendarSelection" class="calendar-selection" tabindex="-1">
              <header>
                <div>
                  <small>Compromissos do dia</small>
                  <strong>{{ formatCalendarSelectionDate(selectedCalendarDay.date) }}</strong>
                </div>
                <span>{{ selectedCalendarDay.items.length }}</span>
              </header>
              <button
                v-for="item in selectedCalendarDay.items"
                :key="`${selectedCalendarDay.key}-${item.id}`"
                type="button"
                @click="openDetails(item)"
              >
                <span>
                  <strong>{{ item.name }}</strong>
                  <small>{{ item.category?.name || 'Sem categoria' }}</small>
                </span>
                <b>{{ privateValue(money(item.amount)) }}</b>
                <i class="ph ph-caret-right" aria-hidden="true"></i>
              </button>
            </div>

            <div v-else-if="!calendarMonthOccurrenceCount" class="calendar-empty">
              <i class="ph ph-calendar-blank" aria-hidden="true"></i>
              <span>Nenhum compromisso neste mês.</span>
            </div>
          </template>
        </div>
      </template>

      <template v-else-if="getFinanceRecurringExpenses.length">
        <div class="agenda-list">
          <article
            v-for="item in getFinanceRecurringExpenses"
            :key="item.id"
            class="fixed-item"
            :class="{ 'is-overdue': isOverdue(item.next_due_date) }"
            role="button"
            tabindex="0"
            :aria-label="`Ver detalhes de ${item.name}`"
            @click="openDetails(item)"
            @keydown.enter="openDetails(item)"
            @keydown.space.prevent="openDetails(item)"
          >
              <span class="date-badge" :class="{ 'is-empty': !item.next_due_date }">
                <template v-if="item.next_due_date">
                  <strong>{{ dateParts(item.next_due_date).day }}</strong>
                  <small>{{ dateParts(item.next_due_date).month }}</small>
                </template>
                <i v-else class="ph ph-calendar-blank" aria-hidden="true"></i>
              </span>

              <div class="fixed-item__main">
                <strong>{{ item.name }}</strong>
                <span>{{ item.category?.name || 'Sem categoria' }} <b aria-hidden="true">•</b> {{ frequencyLabel(item.frequency) }}</span>
              </div>

              <div class="fixed-item__due" :class="{ 'is-overdue': isOverdue(item.next_due_date) }">
                <small>Vencimento</small>
                <span>{{ dueLabel(item.next_due_date) }}</span>
              </div>

              <div class="fixed-item__amount">
                <strong>{{ privateValue(money(item.amount)) }}</strong>
                <small>{{ frequencyShortLabel(item.frequency) }}</small>
              </div>

              <i class="ph ph-caret-right fixed-item__arrow" aria-hidden="true"></i>
          </article>
        </div>

        <div class="pagination-row">
          <span class="pagination-row__summary">{{ paginationSummary }}</span>
          <div class="pagination-actions">
            <button type="button" :disabled="currentPage <= 1" @click="goToPage(currentPage - 1)">Anterior</button>
            <button
              v-for="page in paginationPages"
              :key="page"
              type="button"
              :class="{ active: page === currentPage }"
              :aria-current="page === currentPage ? 'page' : undefined"
              @click="goToPage(page)"
            >
              {{ page }}
            </button>
            <button type="button" :disabled="currentPage >= lastPage" @click="goToPage(currentPage + 1)">Próxima</button>
          </div>

          <div class="pagination-mobile">
            <button type="button" aria-label="Página anterior" :disabled="currentPage <= 1" @click="goToPage(currentPage - 1)">
              <i class="ph ph-caret-left" aria-hidden="true"></i>
            </button>
            <span>Página {{ currentPage }} de {{ lastPage }}</span>
            <button type="button" aria-label="Próxima página" :disabled="currentPage >= lastPage" @click="goToPage(currentPage + 1)">
              <i class="ph ph-caret-right" aria-hidden="true"></i>
            </button>
          </div>
        </div>
      </template>

      <div v-else-if="!getFinanceError" class="empty-fixed">
        <span><i :class="filters.status === 'all' ? 'ph ph-calendar-plus' : 'ph ph-check-circle'" aria-hidden="true"></i></span>
        <strong>{{ emptyStateTitle }}</strong>
        <p>{{ emptyStateDescription }}</p>
        <button v-if="filters.status !== 'all'" type="button" @click="changeAgendaTab('all')">Ver todos os compromissos</button>
      </div>
      </section>
    </div>

    <div v-if="selectedRecurring" class="fixed-modal" role="dialog" aria-modal="true" aria-labelledby="detail-title">
      <div class="fixed-modal__backdrop" @click="closeDetails"></div>
      <section class="fixed-modal__content detail-sheet">
        <span class="fixed-modal__handle" aria-hidden="true"></span>
        <header class="fixed-modal__header">
          <div>
            <span class="fixed-eyebrow">Detalhes</span>
            <h2 id="detail-title">{{ selectedRecurring.name }}</h2><small>{{ selectedRecurring.owner?.name }}</small>
          </div>
          <button type="button" aria-label="Fechar detalhes" @click="closeDetails"><i class="ph ph-x" aria-hidden="true"></i></button>
        </header>

        <div class="detail-amount" :class="{ 'is-overdue': isOverdue(selectedRecurring.next_due_date) }">
          <span><i class="ph ph-calendar-check" aria-hidden="true"></i></span>
          <div>
            <small>{{ frequencyLabel(selectedRecurring.frequency) }}</small>
            <strong>{{ privateValue(money(selectedRecurring.amount)) }}</strong>
          </div>
        </div>

        <dl class="detail-grid">
          <div><dt>Categoria</dt><dd>{{ selectedRecurring.category?.name || 'Sem categoria' }}</dd></div>
          <div><dt>Próximo vencimento</dt><dd>{{ formatDate(selectedRecurring.next_due_date) }}</dd></div>
          <div><dt>Situação</dt><dd :class="{ 'is-overdue': isOverdue(selectedRecurring.next_due_date) }">{{ dueLabel(selectedRecurring.next_due_date) }}</dd></div>
        </dl>

        <div class="detail-actions">
          <button v-if="selectedRecurring.can_edit !== false" type="button" @click="openEditModal(selectedRecurring)"><i class="ph ph-pencil-simple" aria-hidden="true"></i> Editar</button>
          <button v-if="selectedRecurring.can_edit !== false" class="is-danger" type="button" @click="requestDelete(selectedRecurring)"><i class="ph ph-trash" aria-hidden="true"></i> Excluir</button>
        </div>
      </section>
    </div>

    <div v-if="showFormModal" class="fixed-modal" role="dialog" aria-modal="true" aria-labelledby="form-title">
      <div class="fixed-modal__backdrop" @click="closeFormModal"></div>
      <form class="fixed-modal__content" @submit.prevent="submitRecurring">
        <span class="fixed-modal__handle" aria-hidden="true"></span>
        <header class="fixed-modal__header">
          <div>
            <span class="fixed-eyebrow">{{ editingId ? 'Editar compromisso' : 'Novo compromisso' }}</span>
            <h2 id="form-title">{{ editingId ? 'Atualize os detalhes' : 'O que se repete?' }}</h2>
          </div>
          <button type="button" aria-label="Fechar" @click="closeFormModal"><i class="ph ph-x" aria-hidden="true"></i></button>
        </header>

        <div class="fixed-form-grid">
          <label class="field-wide">
            <span>Nome</span>
            <input v-model.trim="form.name" class="form-control" maxlength="120" required placeholder="Ex: aluguel, internet" />
          </label>
          <label class="field-wide recurring-amount-field">
            <span>Valor</span>
            <div>
              <span>R$</span>
              <input
                v-model="amountInput"
                type="text"
                inputmode="numeric"
                autocomplete="off"
                placeholder="0,00"
                aria-label="Valor do compromisso"
                required
                @input="sanitizeAmountInput"
                @paste.prevent="handleAmountPaste"
              />
            </div>
          </label>
          <label>
            <span>Frequência</span>
            <select v-model="form.frequency" class="form-select">
              <option v-for="option in frequencyOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
            </select>
          </label>
          <label>
            <span>Categoria</span>
            <select v-model="form.category_id" class="form-select">
              <option :value="null">Outros (automático)</option>
              <option v-for="category in getFinanceCategories" :key="category.id" :value="category.id">{{ category.name }}</option>
            </select>
          </label>
          <label>
            <span>Próximo vencimento</span>
            <input v-model="form.next_due_date" type="date" class="form-control" />
          </label>
        </div>

        <button class="save-fixed-button" type="submit" :disabled="saving">
          <span>{{ saving ? 'Salvando...' : editingId ? 'Salvar alterações' : 'Salvar compromisso' }}</span>
          <i :class="saving ? 'ph ph-spinner-gap is-spinning' : 'ph ph-arrow-right'" aria-hidden="true"></i>
        </button>
        <div v-if="feedback" class="fixed-feedback fixed-feedback--error mt-15" role="alert">{{ feedback }}</div>
      </form>
    </div>

    <div v-if="pendingDelete" class="confirm-modal" role="alertdialog" aria-modal="true" aria-labelledby="confirm-title">
      <div class="confirm-modal__backdrop" @click="cancelDelete"></div>
      <section class="confirm-modal__content">
        <span class="confirm-modal__icon"><i class="ph ph-trash" aria-hidden="true"></i></span>
        <h2 id="confirm-title">Excluir compromisso?</h2>
        <p>“{{ pendingDelete.name }}” sairá da agenda e dos resumos, mas o histórico será preservado.</p>
        <div>
          <button type="button" :disabled="deleting" @click="cancelDelete">Cancelar</button>
          <button class="is-danger" type="button" :disabled="deleting" @click="confirmDelete">{{ deleting ? 'Excluindo...' : 'Sim, excluir' }}</button>
        </div>
      </section>
    </div>
  </main>
</template>

<script lang="ts">
/* eslint-disable @typescript-eslint/no-explicit-any */
import { defineComponent } from "vue";
import { mapActions, mapGetters } from "vuex";
import FinancialPrivacyButton from "../../components/Financial/FinancialPrivacyButton.vue";

interface RecurringExpense {
  owner?: { name: string };
  can_edit?: boolean;
  id: number;
  name: string;
  amount: number | string;
  frequency: string;
  next_due_date?: string | null;
  category_id?: number | null;
  category?: { id: number; name: string } | null;
}

interface CalendarDay {
  key: string;
  date: Date;
  day: number;
  inMonth: boolean;
  isToday: boolean;
  isPast: boolean;
  items: RecurringExpense[];
}

export default defineComponent({
  name: "RecurringExpensesPage",
  components: { FinancialPrivacyButton },
  data() {
    return {
      saving: false,
      deleting: false,
      feedback: "",
      showFormModal: false,
      editingId: null as number | null,
      selectedRecurring: null as RecurringExpense | null,
      pendingDelete: null as RecurringExpense | null,
      agendaView: "list" as "list" | "calendar",
      calendarDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
      calendarExpenses: [] as RecurringExpense[],
      calendarLoading: false,
      calendarError: "",
      calendarRequestId: 0,
      selectedCalendarDateKey: "",
      amountInput: "",
      form: {
        name: "",
        amount: "",
        category_id: null as number | null,
        frequency: "monthly",
        next_due_date: "",
      },
      filters: { page: 1, per_page: 10, status: "all" },
    };
  },
  computed: {
    ...mapGetters([
      "getFinanceCategories",
      "getFinanceRecurringExpenses",
      "getFinanceRecurringExpensesPagination",
      "getFinanceRecurringExpensesSummary",
      "getFinanceValuesVisible",
      "getLoadingFinance",
      "getFinanceError",
    ]),
    currentPage(): number {
      return Number((this as any).getFinanceRecurringExpensesPagination?.current_page || 1);
    },
    lastPage(): number {
      return Number((this as any).getFinanceRecurringExpensesPagination?.last_page || 1);
    },
    paginationPages(): number[] {
      const start = Math.max(1, (this as any).currentPage - 2);
      const end = Math.min((this as any).lastPage, (this as any).currentPage + 2);
      return Array.from({ length: Math.max(0, end - start + 1) }, (_, index) => start + index);
    },
    paginationSummary(): string {
      if ((this as any).getLoadingFinance) return "Carregando compromissos...";
      const pagination = (this as any).getFinanceRecurringExpensesPagination || {};
      const total = Number(pagination.total || 0);
      if (!total) return "Nenhum compromisso cadastrado";
      const from = pagination.from || (((this as any).currentPage - 1) * Number(pagination.per_page || 10)) + 1;
      const to = pagination.to || Math.min(total, (this as any).currentPage * Number(pagination.per_page || 10));
      return `Mostrando ${from} a ${to} de ${total}`;
    },
    calendarWeekdays(): string[] {
      return ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
    },
    calendarMonthLabel(): string {
      const label = new Intl.DateTimeFormat("pt-BR", { month: "long", year: "numeric" }).format(this.calendarDate);
      return label.charAt(0).toUpperCase() + label.slice(1);
    },
    calendarDays(): CalendarDay[] {
      const year = this.calendarDate.getFullYear();
      const month = this.calendarDate.getMonth();
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      const visibleDays = Math.ceil((firstDay.getDay() + lastDay.getDate()) / 7) * 7;
      const gridStart = new Date(year, month, 1 - firstDay.getDay());
      const today = new Date();
      const todayKey = this.localDateKey(today);
      const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();

      return Array.from({ length: visibleDays }, (_, index) => {
        const date = new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + index);
        const key = this.localDateKey(date);
        return {
          key,
          date,
          day: date.getDate(),
          inMonth: date.getMonth() === month,
          isToday: key === todayKey,
          isPast: date.getTime() < todayStart,
          items: this.calendarExpenses.filter((item) => this.recurringOccursOnDate(item, date)),
        };
      });
    },
    calendarMonthOccurrenceCount(): number {
      return this.calendarDays
        .filter((day) => day.inMonth)
        .reduce((total, day) => total + day.items.length, 0);
    },
    calendarSummary(): string {
      if (this.calendarLoading) return "Carregando calendário...";
      const count = this.calendarMonthOccurrenceCount;
      return `${count} ${count === 1 ? 'compromisso' : 'compromissos'} neste mês`;
    },
    selectedCalendarDay(): CalendarDay | null {
      return this.calendarDays.find((day) => day.key === this.selectedCalendarDateKey && day.items.length > 0) || null;
    },
    monthlyEstimate(): string {
      return (this as any).getFinanceRecurringExpensesSummary?.monthly_estimate?.formatted || "R$ 0,00";
    },
    activeCount(): number {
      return Number((this as any).getFinanceRecurringExpensesSummary?.active_count || 0);
    },
    overdueCount(): number {
      return Number((this as any).getFinanceRecurringExpensesSummary?.overdue_count || 0);
    },
    upcomingCount(): number {
      return Number((this as any).getFinanceRecurringExpensesSummary?.upcoming_count || 0);
    },
    unestimatedCount(): number {
      return Number((this as any).getFinanceRecurringExpensesSummary?.unestimated_count || 0);
    },
    agendaTabs(): Array<{ value: string; label: string; count: number }> {
      return [
        { value: "all", label: "Todos", count: (this as any).activeCount },
        { value: "overdue", label: "Passados", count: (this as any).overdueCount },
        { value: "upcoming", label: "Próximos", count: (this as any).upcomingCount },
      ];
    },
    emptyStateTitle(): string {
      if (this.filters.status === "overdue") return "Nenhuma data passada";
      if (this.filters.status === "upcoming") return "Nenhum próximo vencimento";
      return "Sua agenda está livre";
    },
    emptyStateDescription(): string {
      if (this.filters.status === "overdue") return "Não há compromissos com data anterior a hoje.";
      if (this.filters.status === "upcoming") return "Não há pagamentos futuros com data definida.";
      return "Cadastre aluguel, internet e outros pagamentos que se repetem.";
    },
    frequencyOptions(): Array<{ value: string; label: string }> {
      const options = [
        { value: "monthly", label: "Mensal" },
        { value: "weekly", label: "Semanal" },
        { value: "yearly", label: "Anual" },
      ];
      if (this.form.frequency && !options.some((option) => option.value === this.form.frequency)) {
        options.push({ value: this.form.frequency, label: `${this.form.frequency} (personalizada)` });
      }
      return options;
    },
  },
  mounted() {
    this.loadData();
  },
  methods: {
    ...mapActions([
      "fetchFinanceCategories",
      "fetchFinanceRecurringExpenses",
      "fetchFinanceRecurringExpensesList",
      "createFinanceRecurringExpense",
      "updateFinanceRecurringExpense",
      "deleteFinanceRecurringExpense",
    ]),
    loadData() {
      const requests: Promise<any>[] = [
        (this as any).fetchFinanceCategories(),
        (this as any).fetchFinanceRecurringExpenses(this.requestParams()),
      ];
      if (this.agendaView === "calendar") requests.push(this.loadCalendarData());
      return Promise.all(requests);
    },
    requestParams(page?: number) {
      return { page: page || this.filters.page, per_page: this.filters.per_page, status: this.filters.status };
    },
    changeAgendaTab(status: string) {
      if (status === this.filters.status || (this as any).getLoadingFinance) return;
      this.filters.status = status;
      this.filters.page = 1;
      (this as any).fetchFinanceRecurringExpenses(this.requestParams(1));
      if (this.agendaView === "calendar") this.loadCalendarData();
    },
    toggleAgendaView() {
      this.agendaView = this.agendaView === "list" ? "calendar" : "list";
      this.selectedCalendarDateKey = "";
      if (this.agendaView === "calendar") this.loadCalendarData();
    },
    async loadCalendarData() {
      const requestId = ++this.calendarRequestId;
      this.calendarLoading = true;
      this.calendarError = "";
      try {
        const items = await (this as any).fetchFinanceRecurringExpensesList({ status: this.filters.status });
        if (requestId !== this.calendarRequestId) return;
        this.calendarExpenses = Array.isArray(items) ? items : [];
      } catch (error) {
        if (requestId !== this.calendarRequestId) return;
        this.calendarExpenses = [];
        this.calendarError = "Não foi possível carregar o calendário.";
      } finally {
        if (requestId === this.calendarRequestId) this.calendarLoading = false;
      }
    },
    changeCalendarMonth(offset: number) {
      this.calendarDate = new Date(this.calendarDate.getFullYear(), this.calendarDate.getMonth() + offset, 1);
      this.selectedCalendarDateKey = "";
    },
    selectCalendarDay(day: CalendarDay) {
      if (!day.inMonth) {
        this.calendarDate = new Date(day.date.getFullYear(), day.date.getMonth(), 1);
      }
      this.selectedCalendarDateKey = day.items.length ? day.key : "";
      if (!day.items.length) return;

      this.$nextTick(() => {
        requestAnimationFrame(() => requestAnimationFrame(() => {
          const selection = this.$refs.calendarSelection as HTMLElement | undefined;
          if (!selection) return;
          const mobileNavigation = document.querySelector(".mobile-bottom-nav") as HTMLElement | null;
          const navigationHeight = mobileNavigation?.getBoundingClientRect().height || 0;
          const reservedSpace = navigationHeight + 18;
          const selectionBottom = window.scrollY + selection.getBoundingClientRect().bottom;
          const targetScroll = Math.max(0, selectionBottom - window.innerHeight + reservedSpace);

          selection.focus({ preventScroll: true });
          window.scrollTo({
            top: targetScroll,
            behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
          });
        }));
      });
    },
    calendarDayAriaLabel(day: CalendarDay) {
      const date = new Intl.DateTimeFormat("pt-BR", { day: "numeric", month: "long", year: "numeric" }).format(day.date);
      if (!day.items.length) return `${date}, nenhum compromisso`;
      return `${date}, ${day.items.length} ${day.items.length === 1 ? 'compromisso' : 'compromissos'}`;
    },
    formatCalendarSelectionDate(date: Date) {
      const label = new Intl.DateTimeFormat("pt-BR", { weekday: "long", day: "numeric", month: "long" }).format(date);
      return label.charAt(0).toUpperCase() + label.slice(1);
    },
    openCreateModal() {
      this.resetForm();
      this.editingId = null;
      this.feedback = "";
      this.showFormModal = true;
    },
    openEditModal(item: RecurringExpense) {
      if (item.can_edit === false) return;
      this.editingId = item.id;
      this.form.name = item.name || "";
      this.form.amount = String(item.amount || "");
      this.amountInput = this.formatCurrencyValue(Number(item.amount) || 0);
      this.form.category_id = item.category_id || item.category?.id || null;
      this.form.frequency = item.frequency || "monthly";
      this.form.next_due_date = this.dateInputValue(item.next_due_date);
      this.feedback = "";
      this.selectedRecurring = null;
      this.showFormModal = true;
    },
    closeFormModal() {
      if (this.saving) return;
      this.showFormModal = false;
      this.feedback = "";
    },
    openDetails(item: RecurringExpense) {
      this.selectedRecurring = item;
    },
    closeDetails() {
      this.selectedRecurring = null;
    },
    requestDelete(item: RecurringExpense) {
      if (item.can_edit === false) return;
      this.selectedRecurring = null;
      this.pendingDelete = item;
    },
    cancelDelete() {
      if (this.deleting) return;
      this.pendingDelete = null;
    },
    async confirmDelete() {
      if (!this.pendingDelete || this.deleting) return;
      const shouldGoBack = (this as any).getFinanceRecurringExpenses.length === 1 && (this as any).currentPage > 1;
      this.deleting = true;
      try {
        await (this as any).deleteFinanceRecurringExpense(this.pendingDelete.id);
        this.pendingDelete = null;
        if (shouldGoBack) this.goToPage((this as any).currentPage - 1);
        if (this.agendaView === "calendar") await this.loadCalendarData();
      } finally {
        this.deleting = false;
      }
    },
    resetForm() {
      this.form.name = "";
      this.form.amount = "";
      this.amountInput = "";
      this.form.category_id = null;
      this.form.frequency = "monthly";
      this.form.next_due_date = "";
    },
    async submitRecurring() {
      if (this.saving) return;
      const amount = this.parseCurrencyInput(this.amountInput);
      if (amount === null || amount <= 0) {
        this.feedback = "Informe um valor válido maior que zero.";
        return;
      }

      this.saving = true;
      this.feedback = "";
      const data = {
        ...this.form,
        amount: amount.toFixed(2),
        category_id: this.form.category_id || null,
        next_due_date: this.form.next_due_date || null,
      };
      try {
        if (this.editingId) {
          await (this as any).updateFinanceRecurringExpense({ id: this.editingId, data });
        } else {
          await (this as any).createFinanceRecurringExpense(data);
        }
        this.showFormModal = false;
        this.editingId = null;
        this.resetForm();
        if (this.agendaView === "calendar") await this.loadCalendarData();
      } catch (error) {
        this.feedback = "Não foi possível salvar. Confira os dados e tente novamente.";
      } finally {
        this.saving = false;
      }
    },
    sanitizeAmountInput(event: Event) {
      const input = event.target as HTMLInputElement;
      const digits = input.value
        .replace(/\D/g, "")
        .replace(/^0+(?=\d)/, "")
        .slice(0, 12);
      const formatted = digits
        ? this.formatCurrencyValue(Number(digits) / 100)
        : "";
      this.amountInput = formatted;
      if (input.value !== formatted) input.value = formatted;
    },
    handleAmountPaste(event: ClipboardEvent) {
      const amount = this.parseCurrencyInput(event.clipboardData?.getData("text") || "");
      if (amount === null || amount <= 0) {
        this.amountInput = "";
        return;
      }
      this.amountInput = this.formatCurrencyValue(amount);
    },
    formatCurrencyValue(amount: number): string {
      return new Intl.NumberFormat("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    },
    parseCurrencyInput(value: string): number | null {
      const cleaned = String(value || "").replace(/\s/g, "");
      if (!/\d/.test(cleaned)) return null;

      const commaIndex = cleaned.lastIndexOf(",");
      const dotIndex = cleaned.lastIndexOf(".");
      let normalized = "";

      if (commaIndex >= 0 && dotIndex >= 0) {
        const decimalIndex = Math.max(commaIndex, dotIndex);
        const integer = cleaned.slice(0, decimalIndex).replace(/\D/g, "") || "0";
        const decimal = cleaned.slice(decimalIndex + 1).replace(/\D/g, "").slice(0, 2);
        normalized = `${integer}.${decimal.padEnd(2, "0")}`;
      } else if (commaIndex >= 0) {
        const integer = cleaned.slice(0, commaIndex).replace(/\D/g, "") || "0";
        const decimal = cleaned.slice(commaIndex + 1).replace(/\D/g, "").slice(0, 2);
        normalized = `${integer}.${decimal.padEnd(2, "0")}`;
      } else if (dotIndex >= 0) {
        const decimalPart = cleaned.slice(dotIndex + 1).replace(/\D/g, "");
        const dotCount = (cleaned.match(/\./g) || []).length;
        if (decimalPart.length > 0 && decimalPart.length <= 2) {
          const integer = cleaned.slice(0, dotIndex).replace(/\D/g, "") || "0";
          normalized = `${integer}.${decimalPart.padEnd(2, "0")}`;
        } else if (dotCount >= 1) {
          normalized = cleaned.replace(/\D/g, "");
        }
      } else {
        normalized = cleaned.replace(/\D/g, "");
      }

      const amount = Number(normalized);
      return Number.isFinite(amount) && amount <= 9999999999.99 ? amount : null;
    },
    goToPage(page: number) {
      if (page < 1 || page > (this as any).lastPage || page === (this as any).currentPage) return;
      this.filters.page = page;
      (this as any).fetchFinanceRecurringExpenses(this.requestParams(page));
    },
    parseDate(value?: string | null): Date | null {
      if (!value) return null;
      const dateOnly = String(value).slice(0, 10);
      const parts = dateOnly.split("-").map(Number);
      if (parts.length !== 3 || parts.some((part) => !Number.isFinite(part))) return null;
      return new Date(parts[0], parts[1] - 1, parts[2]);
    },
    localDateKey(date: Date): string {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    recurringOccursOnDate(item: RecurringExpense, date: Date): boolean {
      const anchor = this.parseDate(item.next_due_date);
      if (!anchor) return false;

      const target = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      const anchorDay = new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate());
      if (target.getTime() < anchorDay.getTime()) return false;

      if (item.frequency === "weekly") {
        const days = Math.round((target.getTime() - anchorDay.getTime()) / 86400000);
        return days % 7 === 0;
      }

      if (item.frequency === "monthly") {
        const lastDayOfMonth = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
        return target.getDate() === Math.min(anchorDay.getDate(), lastDayOfMonth);
      }

      if (item.frequency === "yearly") {
        if (target.getMonth() !== anchorDay.getMonth()) return false;
        const lastDayOfMonth = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
        return target.getDate() === Math.min(anchorDay.getDate(), lastDayOfMonth);
      }

      return this.localDateKey(target) === this.localDateKey(anchorDay);
    },
    dateInputValue(value?: string | null) {
      return value ? String(value).slice(0, 10) : "";
    },
    isOverdue(value?: string | null) {
      const date = this.parseDate(value);
      if (!date) return false;
      const today = new Date();
      return date.getTime() < new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
    },
    dueLabel(value?: string | null) {
      const date = this.parseDate(value);
      if (!date) return "Sem vencimento";
      const today = new Date();
      const base = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
      const difference = Math.round((date.getTime() - base) / 86400000);
      if (difference < 0) return `Há ${Math.abs(difference)} ${Math.abs(difference) === 1 ? 'dia' : 'dias'}`;
      if (difference === 0) return "Vence hoje";
      if (difference === 1) return "Vence amanhã";
      if (difference <= 7) return `Em ${difference} dias`;
      return this.formatDate(value);
    },
    dateParts(value?: string | null) {
      const date = this.parseDate(value);
      if (!date) return { day: "--", month: "---" };
      return {
        day: new Intl.DateTimeFormat("pt-BR", { day: "2-digit" }).format(date),
        month: new Intl.DateTimeFormat("pt-BR", { month: "short" }).format(date).replace(".", "").toUpperCase(),
      };
    },
    formatDate(value?: string | null) {
      const date = this.parseDate(value);
      return date ? new Intl.DateTimeFormat("pt-BR", { day: "2-digit", month: "short", year: "numeric" }).format(date) : "Sem vencimento";
    },
    money(value: number | string) {
      return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(value) || 0);
    },
    privateValue(value: string) {
      return (this as any).getFinanceValuesVisible ? value : "••••••";
    },
    frequencyLabel(value: string) {
      return ({ monthly: "Mensal", weekly: "Semanal", yearly: "Anual" } as Record<string, string>)[value] || value || "Personalizada";
    },
    frequencyShortLabel(value: string) {
      return ({ monthly: "por mês", weekly: "por semana", yearly: "por ano" } as Record<string, string>)[value] || "frequência personalizada";
    },
  },
});
</script>

<style scoped>
.fixed-page {
  --fixed-accent: #06a77d;
  --fixed-accent-dark: #05785d;
  --fixed-ink: #171629;
  --fixed-muted: #747487;
  --fixed-surface: #ffffff;
  --fixed-soft: #f5f5f8;
  --fixed-border: #e9e9f0;
  --fixed-danger: #c74f4a;
  color: var(--fixed-ink);
  margin: 0 auto;
  max-width: 1480px;
  width: 100%;
}

.fixed-header { align-items: center; display: flex; gap: 20px; justify-content: space-between; margin-bottom: 18px; }
.fixed-header__actions { align-items:center; display:flex; gap:8px; }
.fixed-eyebrow { color: var(--fixed-accent-dark); display: block; font-size: 11px; font-weight: 800; letter-spacing: 0.08em; margin-bottom: 5px; text-transform: uppercase; }
.fixed-header h1 { color: var(--fixed-ink); font-size: clamp(24px, 3vw, 32px); font-weight: 800; letter-spacing: -0.04em; margin: 0; }
.refresh-button { align-items: center; background: var(--fixed-surface); border: 1px solid var(--fixed-border); border-radius: 50%; color: var(--fixed-ink); display: inline-flex; flex: 0 0 44px; font-size: 20px; height: 44px; justify-content: center; transition: 0.2s ease; }
.refresh-button:hover:not(:disabled) { border-color: var(--fixed-accent); color: var(--fixed-accent-dark); transform: translateY(-1px); }
.refresh-button:disabled { cursor: wait; opacity: 0.65; }
.is-spinning { animation: fixed-spin 0.9s linear infinite; }

.fixed-workspace { background: var(--fixed-surface); border: 1px solid var(--fixed-border); border-radius: 22px; box-shadow: 0 10px 30px rgba(23,22,41,0.035); overflow: hidden; }
.estimate-card { display: grid; gap: 24px; grid-template-columns: minmax(0,1fr) 210px; min-height: 172px; overflow: hidden; padding: 24px 24px 24px 30px; position: relative; }
.estimate-card::before { background: var(--fixed-accent); border-radius: 0 999px 999px 0; bottom: 24px; content: ""; left: 0; position: absolute; top: 24px; width: 5px; }
.estimate-card__content { min-width: 0; position: relative; z-index: 2; }
.estimate-card__label { color: var(--fixed-accent-dark); font-size: 11px; font-weight: 800; letter-spacing: 0.08em; text-transform: uppercase; }
.estimate-card h2 { color: var(--fixed-ink); font-size: clamp(32px, 5vw, 43px); font-weight: 800; letter-spacing: -0.055em; margin: 9px 0 2px; }
.estimate-card p { color: var(--fixed-muted); font-size: 12px; margin: 0; }
.estimate-card__aside { align-items: center; align-self: stretch; background: linear-gradient(145deg, #e8f8f3 0%, #f3fbf8 100%); border: 1px solid #d8f1e9; border-radius: 17px; display: flex; flex-direction: column; justify-content: center; padding: 18px; text-align: center; }
.estimate-card__aside > span { align-items: center; background: var(--fixed-surface); border: 1px solid #d2eee5; border-radius: 15px; color: var(--fixed-accent-dark); display: flex; font-size: 29px; height: 58px; justify-content: center; margin-bottom: 10px; width: 58px; }
.estimate-card__aside small, .estimate-card__aside strong { display: block; }
.estimate-card__aside small { color: var(--fixed-muted); font-size: 8px; font-weight: 800; letter-spacing: 0.08em; text-transform: uppercase; }
.estimate-card__aside strong { color: var(--fixed-ink); font-size: 12px; font-weight: 800; margin-top: 3px; }
.estimate-card__add { cursor: pointer; font-family: inherit; transition: 0.2s ease; }
.estimate-card__add:hover { border-color: var(--fixed-accent); box-shadow: 0 8px 20px rgba(6,167,125,0.14); transform: translateY(-1px); }
.estimate-card__add:focus-visible { outline: 3px solid rgba(6,167,125,0.22); outline-offset: 3px; }
.estimate-card__add > span i { font-size: 25px; }
.estimate-warning { color: var(--fixed-muted); display: block; font-size: 10px; margin-top: 10px; }

.agenda-panel { border-top: 1px solid var(--fixed-border); padding: 22px; }
.agenda-heading { align-items: flex-start; display: flex; gap: 18px; justify-content: space-between; }
.agenda-heading h2 { color: var(--fixed-ink); font-size: 21px; font-weight: 800; letter-spacing: -0.025em; margin: 0; }
.agenda-heading p { color: var(--fixed-muted); font-size: 12px; margin: 4px 0 0; }
.agenda-heading__actions { align-items: center; display: flex; gap: 12px; }
.agenda-heading__hint { align-items: center; color: var(--fixed-muted); display: inline-flex; font-size: 10px; gap: 4px; margin-top: 13px; }
.agenda-view-toggle { align-items: center; background: #e8f8f3; border: 1px solid #d2eee5; border-radius: 12px; color: var(--fixed-accent-dark); display: inline-flex; flex: 0 0 42px; font-size: 20px; height: 42px; justify-content: center; transition: 0.2s ease; width: 42px; }
.agenda-view-toggle:hover, .agenda-view-toggle:focus-visible { background: var(--fixed-accent); border-color: var(--fixed-accent); color: #fff; outline: 0; transform: translateY(-1px); }
.agenda-tabs { background: var(--fixed-soft); border-radius: 15px; display: grid; gap: 5px; grid-template-columns: repeat(3,minmax(0,1fr)); margin-top: 20px; padding: 5px; }
.agenda-tabs button { align-items: center; background: transparent; border: 0; border-radius: 11px; color: var(--fixed-muted); display: flex; font-size: 11px; font-weight: 800; gap: 6px; justify-content: center; min-height: 42px; padding: 0 10px; transition: 0.2s ease; }
.agenda-tabs button span { align-items: center; background: rgba(116,116,135,0.1); border-radius: 999px; display: inline-flex; font-size: 9px; height: 20px; justify-content: center; min-width: 20px; padding: 0 6px; }
.agenda-tabs button.active { background: var(--fixed-surface); box-shadow: 0 4px 14px rgba(23,22,41,0.08); color: var(--fixed-accent-dark); }
.agenda-tabs button.active span { background: #e8f8f3; color: var(--fixed-accent-dark); }
.calendar-view { border-top: 1px solid var(--fixed-border); margin-top: 16px; padding-top: 16px; }
.calendar-toolbar { align-items: center; display: grid; grid-template-columns: 38px 1fr 38px; margin-bottom: 13px; }
.calendar-toolbar strong { color: var(--fixed-ink); font-size: 15px; font-weight: 800; text-align: center; }
.calendar-toolbar button { align-items: center; background: var(--fixed-surface); border: 1px solid var(--fixed-border); border-radius: 10px; color: var(--fixed-muted); display: flex; font-size: 15px; height: 38px; justify-content: center; transition: 0.2s ease; width: 38px; }
.calendar-toolbar button:hover, .calendar-toolbar button:focus-visible { border-color: var(--fixed-accent); color: var(--fixed-accent-dark); outline: 0; }
.calendar-grid, .calendar-loading { display: grid; grid-template-columns: repeat(7,minmax(0,1fr)); }
.calendar-grid { border-left: 1px solid var(--fixed-border); border-top: 1px solid var(--fixed-border); }
.calendar-weekday { color: var(--fixed-muted); font-size: 9px; font-weight: 800; padding: 8px 2px; text-align: center; text-transform: uppercase; }
.calendar-day { align-items: flex-start; background: var(--fixed-surface); border: 0; border-bottom: 1px solid var(--fixed-border); border-right: 1px solid var(--fixed-border); color: var(--fixed-ink); display: flex; flex-direction: column; min-height: 72px; padding: 8px; position: relative; transition: 0.18s ease; }
.calendar-day > span { align-items: center; border-radius: 50%; display: inline-flex; font-size: 11px; font-weight: 700; height: 25px; justify-content: center; width: 25px; }
.calendar-day > strong { align-items: center; align-self: flex-end; background: var(--fixed-accent); border-radius: 999px; color: #fff; display: inline-flex; font-size: 10px; height: 21px; justify-content: center; margin-top: auto; min-width: 21px; padding: 0 6px; }
.calendar-day.is-outside { background: var(--fixed-soft); color: var(--fixed-muted); opacity: 0.48; }
.calendar-day.is-past:not(.is-outside) { background: #fafafd; color: #a4a4b0; }
.calendar-day.is-past > strong { background: #aeb2bb; }
.calendar-day.has-items { cursor: pointer; }
.calendar-day.has-items:hover, .calendar-day.has-items:focus-visible, .calendar-day.is-selected { background: #edfaf6; outline: 0; }
.calendar-day.is-today { background: #e8f8f3; }
.calendar-day.is-today > span { color: var(--fixed-accent-dark); font-weight: 900; }
.calendar-day.is-selected { box-shadow: inset 0 0 0 2px var(--fixed-accent); }
.calendar-loading { border-left: 1px solid var(--fixed-border); border-top: 1px solid var(--fixed-border); }
.calendar-loading .skeleton { border-bottom: 1px solid var(--fixed-border); border-radius: 0; border-right: 1px solid var(--fixed-border); height: 72px; }
.calendar-selection { background: var(--fixed-soft); border-radius: 15px; margin-top: 14px; overflow: hidden; padding: 5px 13px; }
.calendar-selection:focus { outline: 0; }
.calendar-selection > header { align-items: center; display: flex; justify-content: space-between; padding: 10px 2px 7px; }
.calendar-selection > header small, .calendar-selection > header strong { display: block; }
.calendar-selection > header small { color: var(--fixed-muted); font-size: 8px; font-weight: 800; letter-spacing: 0.07em; text-transform: uppercase; }
.calendar-selection > header strong { color: var(--fixed-ink); font-size: 13px; margin-top: 2px; }
.calendar-selection > header > span { align-items: center; background: #e2f6f0; border-radius: 999px; color: var(--fixed-accent-dark); display: inline-flex; font-size: 10px; font-weight: 800; height: 24px; justify-content: center; min-width: 24px; padding: 0 7px; }
.calendar-selection > button { align-items: center; background: transparent; border: 0; border-top: 1px solid var(--fixed-border); color: var(--fixed-ink); display: grid; gap: 10px; grid-template-columns: minmax(0,1fr) auto 14px; min-height: 54px; padding: 8px 2px; text-align: left; width: 100%; }
.calendar-selection > button > span { min-width: 0; }
.calendar-selection > button strong, .calendar-selection > button small { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.calendar-selection > button strong { font-size: 12px; }
.calendar-selection > button small { color: var(--fixed-muted); font-size: 9px; margin-top: 3px; }
.calendar-selection > button b { font-size: 11px; white-space: nowrap; }
.calendar-selection > button i { color: var(--fixed-muted); }
.calendar-empty { align-items: center; color: var(--fixed-muted); display: flex; font-size: 11px; gap: 7px; justify-content: center; padding: 18px 0 2px; }
.calendar-empty i { color: var(--fixed-accent-dark); font-size: 17px; }
.agenda-list { border-top: 1px solid var(--fixed-border); margin-top: 16px; }
.fixed-item { align-items: center; border-bottom: 1px solid var(--fixed-border); cursor: pointer; display: grid; gap: 14px; grid-template-columns: 48px minmax(180px, 1.25fr) minmax(150px, 0.8fr) minmax(120px, 0.65fr) 22px; min-height: 78px; outline: 0; padding: 11px 4px; transition: 0.2s ease; }
.fixed-item:hover, .fixed-item:focus-visible { background: rgba(6,167,125,0.045); padding-left: 9px; padding-right: 9px; }
.fixed-item.is-overdue { opacity: 0.58; }
.fixed-item.is-overdue:hover, .fixed-item.is-overdue:focus-visible { background: var(--fixed-soft); opacity: 0.78; }
.date-badge { align-items: center; background: #e8f8f3; border-radius: 13px; color: var(--fixed-accent-dark); display: flex; flex-direction: column; height: 48px; justify-content: center; width: 48px; }
.date-badge strong { font-size: 16px; line-height: 1; }
.date-badge small { font-size: 8px; font-weight: 800; margin-top: 4px; }
.date-badge.is-empty { background: var(--fixed-soft); color: var(--fixed-muted); font-size: 19px; }
.fixed-item.is-overdue .date-badge { background: var(--fixed-soft); color: var(--fixed-muted); }
.fixed-item__main { min-width: 0; }
.fixed-item__main > strong { color: var(--fixed-ink); display: block; font-size: 14px; font-weight: 800; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.fixed-item__main > span { color: var(--fixed-muted); display: block; font-size: 11px; margin-top: 4px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.fixed-item__main b { font-weight: 400; margin: 0 3px; }
.fixed-item__due small, .fixed-item__amount small { color: var(--fixed-muted); display: block; font-size: 9px; font-weight: 700; text-transform: uppercase; }
.fixed-item__due span { color: var(--fixed-ink); display: block; font-size: 12px; font-weight: 700; margin-top: 3px; }
.fixed-item__due.is-overdue span { color: var(--fixed-danger); }
.fixed-item__amount { text-align: right; }
.fixed-item__amount strong { color: var(--fixed-ink); display: block; font-size: 14px; font-weight: 800; white-space: nowrap; }
.fixed-item__amount small { margin-top: 3px; text-transform: none; }
.fixed-item__arrow { color: #aaa8b5; font-size: 15px; }

.pagination-row { align-items: center; display: flex; justify-content: space-between; margin-top: 22px; padding-top: 18px; }
.pagination-row__summary { color: var(--fixed-muted); font-size: 12px; }
.pagination-actions { display: flex; gap: 6px; }
.pagination-actions button, .pagination-mobile button { background: var(--fixed-surface); border: 1px solid var(--fixed-border); border-radius: 9px; color: var(--fixed-muted); font-size: 11px; font-weight: 800; min-height: 36px; min-width: 36px; padding: 0 10px; }
.pagination-actions button.active { background: var(--fixed-accent); border-color: var(--fixed-accent); color: #fff; }
.pagination-actions button:disabled, .pagination-mobile button:disabled { opacity: 0.4; }
.pagination-mobile { display: none; }

.fixed-feedback { align-items: center; border-radius: 13px; display: flex; font-size: 12px; font-weight: 700; justify-content: space-between; margin-top: 18px; padding: 12px 14px; }
.fixed-feedback > div { align-items: center; display: flex; gap: 8px; }
.fixed-feedback button { background: transparent; border: 0; color: inherit; font-weight: 800; }
.fixed-feedback--error { background: #fff1f0; border: 1px solid #ffd5d1; color: #9c3732; }
.empty-fixed { align-items: center; display: flex; flex-direction: column; padding: 54px 16px 40px; text-align: center; }
.empty-fixed > span { align-items: center; background: #e8f8f3; border-radius: 50%; color: var(--fixed-accent-dark); display: flex; font-size: 28px; height: 62px; justify-content: center; margin-bottom: 14px; width: 62px; }
.empty-fixed strong { color: var(--fixed-ink); font-size: 17px; }
.empty-fixed p { color: var(--fixed-muted); margin: 6px 0 16px; }
.empty-fixed button { background: var(--fixed-accent); border: 0; border-radius: 12px; color: #fff; font-weight: 800; min-height: 42px; padding: 0 16px; }
.fixed-skeletons { display: grid; gap: 10px; margin-top: 24px; }
.fixed-skeleton { align-items: center; border-top: 1px solid var(--fixed-border); display: grid; gap: 12px; grid-template-columns: 48px 1fr 110px; padding: 12px 0; }
.skeleton { animation: fixed-pulse 1.25s ease-in-out infinite; background: #eeeeF3; border-radius: 10px; display: block; }
.skeleton--date { height: 48px; }
.skeleton--content { height: 34px; }
.skeleton--amount { height: 24px; }

.fixed-modal, .confirm-modal { align-items: center; display: flex; inset: 0; justify-content: center; padding: 20px; position: fixed; z-index: 1060; }
.fixed-modal__backdrop, .confirm-modal__backdrop { background: rgba(17,20,32,0.56); inset: 0; position: absolute; }
.fixed-modal__content, .confirm-modal__content { background: var(--fixed-surface); box-shadow: 0 24px 80px rgba(15,23,42,0.24); max-height: calc(100vh - 40px); overflow-y: auto; position: relative; z-index: 1; }
.fixed-modal__content { border-radius: 22px; max-width: 590px; padding: 24px; width: min(100%,590px); }
.fixed-modal__handle { display: none; }
.fixed-modal__header { align-items: flex-start; display: flex; justify-content: space-between; margin-bottom: 19px; }
.fixed-modal__header h2 { color: var(--fixed-ink); font-size: 23px; font-weight: 800; letter-spacing: -0.03em; margin: 0; }
.fixed-modal__header button { align-items: center; background: var(--fixed-soft); border: 0; border-radius: 50%; color: var(--fixed-ink); display: flex; flex: 0 0 38px; font-size: 18px; height: 38px; justify-content: center; width: 38px; }
.fixed-form-grid { display: grid; gap: 13px; grid-template-columns: 1fr 1fr; }
.fixed-form-grid .field-wide { grid-column: 1/-1; }
.fixed-form-grid label > span { color: var(--fixed-ink); display: block; font-size: 11px; font-weight: 800; margin-bottom: 7px; }
.fixed-form-grid .form-control, .fixed-form-grid .form-select { background-color: var(--fixed-surface); border: 1px solid var(--fixed-border); border-radius: 12px; color: var(--fixed-ink); font-size: 13px; min-height: 46px; }
.recurring-amount-field > div { align-items: center; border-bottom: 2px solid var(--fixed-border); display: flex; gap: 8px; padding: 2px 0 8px; transition: border-color 0.2s ease; }
.recurring-amount-field > div:focus-within { border-color: var(--fixed-accent); }
.recurring-amount-field > div > span { color: var(--fixed-muted); font-size: 21px; font-weight: 800; }
.recurring-amount-field input { background: transparent; border: 0; color: var(--fixed-ink); font-size: 32px; font-weight: 800; letter-spacing: -0.04em; min-width: 0; outline: 0; width: 100%; }
.recurring-amount-field input::placeholder { color: #c3c2cb; }
.save-fixed-button { align-items: center; background: var(--fixed-accent); border: 0; border-radius: 13px; color: #fff; display: flex; font-size: 14px; font-weight: 800; justify-content: space-between; margin-top: 18px; min-height: 48px; padding: 0 17px; width: 100%; }
.save-fixed-button:disabled { opacity: 0.65; }
.detail-amount { align-items: center; background: #e8f8f3; border-radius: 17px; display: flex; gap: 13px; margin-bottom: 14px; padding: 15px; }
.detail-amount.is-overdue { background: #fff0ee; }
.detail-amount > span { align-items: center; background: var(--fixed-surface); border-radius: 13px; color: var(--fixed-accent-dark); display: flex; font-size: 22px; height: 46px; justify-content: center; width: 46px; }
.detail-amount.is-overdue > span { color: var(--fixed-danger); }
.detail-amount small, .detail-amount strong { display: block; }
.detail-amount small { color: var(--fixed-muted); font-size: 10px; font-weight: 800; text-transform: uppercase; }
.detail-amount strong { color: var(--fixed-ink); font-size: 25px; font-weight: 800; letter-spacing: -0.03em; margin-top: 2px; }
.detail-grid { display: grid; gap: 9px; grid-template-columns: repeat(3,1fr); margin: 0; }
.detail-grid > div { background: var(--fixed-soft); border-radius: 13px; padding: 12px; }
.detail-grid dt { color: var(--fixed-muted); font-size: 9px; font-weight: 800; text-transform: uppercase; }
.detail-grid dd { color: var(--fixed-ink); font-size: 12px; font-weight: 800; margin: 5px 0 0; }
.detail-grid dd.is-overdue { color: var(--fixed-danger); }
.detail-actions { display: grid; gap: 9px; grid-template-columns: 1fr 1fr; margin-top: 16px; }
.detail-actions button { align-items: center; background: var(--fixed-soft); border: 0; border-radius: 12px; color: var(--fixed-ink); display: flex; font-weight: 800; gap: 7px; justify-content: center; min-height: 44px; }
.detail-actions button.is-danger { background: #fff0ee; color: var(--fixed-danger); }
.confirm-modal__content { border-radius: 22px; max-width: 410px; padding: 26px; text-align: center; width: min(100%,410px); }
.confirm-modal__icon { align-items: center; background: #fff0ee; border-radius: 50%; color: var(--fixed-danger); display: flex; font-size: 25px; height: 58px; justify-content: center; margin: 0 auto 14px; width: 58px; }
.confirm-modal h2 { color: var(--fixed-ink); font-size: 21px; font-weight: 800; margin: 0; }
.confirm-modal p { color: var(--fixed-muted); font-size: 13px; line-height: 1.55; margin: 8px 0 18px; }
.confirm-modal__content > div:last-child { display: grid; gap: 9px; grid-template-columns: 1fr 1fr; }
.confirm-modal button { background: var(--fixed-soft); border: 0; border-radius: 12px; color: var(--fixed-ink); font-weight: 800; min-height: 44px; }
.confirm-modal button.is-danger { background: var(--fixed-danger); color: #fff; }

@media (max-width: 767px) {
  .fixed-header { margin-bottom: 14px; }
  .fixed-header h1 { font-size: 24px; }
  .fixed-workspace { border-radius: 20px; }
  .estimate-card { gap: 14px; grid-template-columns: minmax(0,1fr) 64px; min-height: 160px; padding: 21px 17px 18px 23px; }
  .estimate-card::before { bottom: 20px; top: 20px; width: 4px; }
  .estimate-card h2 { font-size: 35px; margin-top: 11px; }
  .estimate-card p { font-size: 10px; line-height: 1.45; max-width: 260px; }
  .estimate-card__aside { align-self: start; background: #e8f8f3; border: 0; border-radius: 15px; min-height: 64px; padding: 0; }
  .estimate-card__aside > span { background: transparent; border: 0; font-size: 28px; height: 64px; margin: 0; width: 64px; }
  .estimate-card__aside > div { display: none; }
  .agenda-panel { padding: 17px 15px; }
  .agenda-heading h2 { font-size: 19px; }
  .agenda-heading__hint { display: none; }
  .agenda-view-toggle { border-radius: 11px; flex-basis: 40px; height: 40px; width: 40px; }
  .agenda-tabs { margin-top: 17px; }
  .agenda-tabs button { font-size: 10px; gap: 4px; min-height: 40px; padding: 0 5px; }
  .agenda-tabs button span { font-size: 8px; height: 18px; min-width: 18px; padding: 0 5px; }
  .calendar-day { min-height: 56px; padding: 5px; }
  .calendar-day > span { font-size: 10px; height: 22px; width: 22px; }
  .calendar-day > strong { font-size: 9px; height: 19px; min-width: 19px; padding: 0 5px; }
  .calendar-loading .skeleton { height: 56px; }
  .calendar-view.has-selection { padding-bottom: max(84px, calc(68px + env(safe-area-inset-bottom))); }
  .fixed-item { gap: 10px; grid-template-columns: 42px minmax(0,1fr) auto 14px; min-height: 72px; padding: 10px 0; }
  .fixed-item:hover, .fixed-item:focus-visible { padding-left: 4px; padding-right: 4px; }
  .date-badge { border-radius: 12px; height: 42px; width: 42px; }
  .date-badge strong { font-size: 14px; }
  .fixed-item__main > strong { font-size: 13px; }
  .fixed-item__main > span { font-size: 10px; }
  .fixed-item__due { display: none; }
  .fixed-item__amount strong { font-size: 12px; }
  .fixed-item__amount small { font-size: 8px; max-width: 90px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .fixed-item__arrow { font-size: 13px; }
  .pagination-row__summary, .pagination-actions { display: none; }
  .pagination-mobile { align-items: center; display: flex; justify-content: space-between; width: 100%; }
  .pagination-mobile span { color: var(--fixed-muted); font-size: 11px; font-weight: 800; }
  .empty-fixed { padding-bottom: 30px; padding-top: 38px; }
  .fixed-modal { align-items: flex-end; padding: 0; }
  .fixed-modal__content { border-radius: 24px 24px 0 0; max-height: calc(100vh - 20px); padding: 14px 18px max(20px,calc(14px + env(safe-area-inset-bottom))); width: 100%; }
  .fixed-modal__handle { background: #d5d4dc; border-radius: 999px; display: block; height: 4px; margin: 0 auto 14px; width: 42px; }
  .fixed-modal__header h2 { font-size: 21px; }
  .fixed-form-grid { gap: 0; grid-template-columns: 1fr; }
  .fixed-form-grid .field-wide { grid-column: auto; }
  .fixed-form-grid label { margin-bottom: 13px; }
  .recurring-amount-field input { font-size: 30px; }
  .detail-grid { gap: 8px; grid-template-columns: 1fr; }
  .detail-grid > div { align-items: center; display: flex; justify-content: space-between; padding: 10px 12px; }
  .detail-grid dd { margin: 0; text-align: right; }
  .confirm-modal { align-items: flex-end; padding: 12px; }
  .confirm-modal__content { border-radius: 22px; padding-bottom: max(24px,calc(14px + env(safe-area-inset-bottom))); }
}

@media (max-width: 374px) {
  .estimate-card h2 { font-size: 33px; }
  .estimate-card { grid-template-columns: minmax(0,1fr) 54px; }
  .estimate-card__aside, .estimate-card__aside > span { height: 54px; min-height: 54px; width: 54px; }
  .fixed-item { gap: 8px; grid-template-columns: 40px minmax(0,1fr) auto 12px; }
  .date-badge { height: 40px; width: 40px; }
  .fixed-item__amount small { max-width: 72px; }
}

body.dark .fixed-page { --fixed-ink: #f5f4fb; --fixed-muted: #aaa8ba; --fixed-surface: #29283b; --fixed-soft: #343347; --fixed-border: #3b394e; }
body.dark .fixed-page .estimate-card__aside { background: rgba(6,167,125,0.13); border-color: rgba(96,213,181,0.18); }
body.dark .fixed-page .estimate-card__aside > span { background: #242335; border-color: rgba(96,213,181,0.2); color: #60d5b5; }
body.dark .fixed-page .agenda-tabs button.active { box-shadow: 0 4px 14px rgba(0,0,0,0.2); }
body.dark .fixed-page .agenda-tabs button.active span { background: rgba(6,167,125,0.16); color: #60d5b5; }
body.dark .fixed-page .agenda-view-toggle { background: rgba(6,167,125,0.16); border-color: rgba(96,213,181,0.2); color: #60d5b5; }
body.dark .fixed-page .calendar-day.is-past:not(.is-outside) { background: rgba(255,255,255,0.018); color: #777689; }
body.dark .fixed-page .calendar-day.is-today { background: rgba(6,167,125,0.16); }
body.dark .fixed-page .calendar-day.has-items:hover, body.dark .fixed-page .calendar-day.has-items:focus-visible, body.dark .fixed-page .calendar-day.is-selected { background: rgba(6,167,125,0.12); }
body.dark .fixed-page .calendar-selection > header > span { background: rgba(6,167,125,0.16); color: #60d5b5; }
body.dark .fixed-page .date-badge, body.dark .fixed-page .empty-fixed > span, body.dark .fixed-page .detail-amount { background: rgba(6,167,125,0.16); color: #60d5b5; }
body.dark .fixed-page .fixed-item.is-overdue .date-badge { background: var(--fixed-soft); color: var(--fixed-muted); }
body.dark .fixed-page .detail-amount.is-overdue, body.dark .fixed-page .detail-actions button.is-danger, body.dark .fixed-page .confirm-modal__icon { background: rgba(199,79,74,0.17); color: #ffaaa4; }
body.dark .fixed-page .fixed-feedback--error { background: rgba(199,79,74,0.15); border-color: rgba(255,170,164,0.22); color: #ffb7b2; }
body.dark .fixed-page .form-control, body.dark .fixed-page .form-select { background-color: #242335; border-color: #444258; color: #f5f4fb; }
body.dark .fixed-page .skeleton { background: #3a394d; }

@keyframes fixed-spin { to { transform: rotate(360deg); } }
@keyframes fixed-pulse { 0%,100% { opacity: 0.55; } 50% { opacity: 1; } }
@media (prefers-reduced-motion: reduce) { .is-spinning, .skeleton { animation: none; } .fixed-item, .estimate-card__add, .refresh-button, .agenda-view-toggle, .calendar-day, .calendar-toolbar button { transition: none; } }
</style>
